import { useEffect, useState } from 'react';

import { UseActiveSectionOptions } from './useActiveSection.types';

export const useActiveSection = (
  sectionIds: string[],
  options: UseActiveSectionOptions = {}
): string => {
  const { offset = 140, defaultActiveId = '' } = options;
  const [activeId, setActiveId] = useState<string>(defaultActiveId);

  const idsKey = sectionIds.join(',');

  useEffect(() => {
    if (!sectionIds || sectionIds.length === 0) {
      return;
    }

    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;

      ticking = true;
      window.requestAnimationFrame(() => {
        const scrollPosition = window.scrollY || window.pageYOffset || 0;
        const windowHeight = window.innerHeight || 0;
        const documentHeight =
          document.documentElement?.scrollHeight || document.body?.scrollHeight || 0;

        const maxScroll = documentHeight - windowHeight;
        const buffer = 0.1 * windowHeight;

        // 1. No meaningful scroll range (page doesn't scroll, or scrolls less than the
        // buffer): never highlight anything.
        if (maxScroll < buffer) {
          setActiveId('');
          ticking = false;
          return;
        }

        // 2. Top dead zone: hasn't scrolled past the buffer yet.
        if (scrollPosition < buffer) {
          setActiveId('');
          ticking = false;
          return;
        }

        // 3. Standard scroll spy loop over absolute (document-relative) offsets: find
        // the last section whose top has been scrolled past the `offset` line.
        //
        // On a page where the viewport is tall relative to the trailing content, the
        // last one or two headings can need MORE scroll than the page actually has —
        // their natural threshold (absoluteTop - offset) exceeds maxScroll, making them
        // mathematically unreachable. Rather than special-casing just the very last
        // heading (which still lets an earlier short one get skipped), rescale the
        // whole scroll range by a single ratio so the last heading's threshold lines up
        // exactly with the true bottom of the page. Every section's relative share of
        // scroll distance — and therefore the order nothing ever skips over — is
        // preserved; only the last stretch of the page compresses to fit.
        const lastId = sectionIds[sectionIds.length - 1];
        const lastElement = document.getElementById(lastId);
        let stretch = 1;
        if (lastElement) {
          const lastAbsoluteTop = lastElement.getBoundingClientRect().top + scrollPosition;
          const lastThreshold = lastAbsoluteTop - offset;
          if (lastThreshold > maxScroll) {
            stretch = lastThreshold / maxScroll;
          }
        }
        const virtualScrollPosition = scrollPosition * stretch;

        let currentActive = sectionIds[0];
        for (const id of sectionIds) {
          const element = document.getElementById(id);
          if (!element) continue;
          const absoluteTop = element.getBoundingClientRect().top + scrollPosition;
          if (absoluteTop <= virtualScrollPosition + offset) {
            currentActive = id;
          } else {
            break;
          }
        }

        setActiveId(currentActive);
        ticking = false;
      });
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, offset]);

  return sectionIds.length === 0 ? '' : activeId;
};
